// Carnet d'entretien des véhicules : échéances et coûts.
//
// Deux questions, et deux seulement :
//   1. QU'EST-CE QUI ARRIVE À ÉCHÉANCE ? contrôle technique, assurance,
//      révision — par date, et pour la révision aussi par kilométrage.
//   2. COMBIEN COÛTE CE VÉHICULE ? le total, la répartition par nature, et le
//      prix de revient au kilomètre quand on peut le calculer.
//
// Une échéance au kilométrage se compare au dernier relevé, qui date : le
// véhicule a roulé depuis. On ne bricole aucune extrapolation — un relevé vieux
// de six mois est signalé comme tel, et c'est au conducteur de le rafraîchir.
//
// Fonctions PURES : aucun accès réseau ni base, donc testables ligne à ligne.

const JOUR_MS = 86_400_000;

/** Ce qu'on suit sur un véhicule. */
export interface VehiculeSuivi {
  id: string;
  immatriculation: string;
  type: string;
  marque: string | null;
  modele: string | null;
  actif: boolean;
  /** Dernier relevé au tableau de bord, en kilomètres. */
  kilometrage: number | null;
  /** Jour du relevé, 'AAAA-MM-JJ'. */
  kilometrageReleveLe: string | null;
  prochaineCt: string | null;
  assuranceExpireLe: string | null;
  assuranceCompagnie: string | null;
  prochaineRevisionLe: string | null;
  prochaineRevisionKm: number | null;
}

/** Un passage au garage. */
export interface InterventionVehicule {
  id: string;
  vehiculeId: string;
  type: string;
  dateIntervention: string;
  kilometrage: number | null;
  libelle: string;
  coutHt: number;
  coutTtc: number;
}

export const TYPES_INTERVENTION = [
  'entretien',
  'reparation',
  'revision',
  'controle_technique',
  'pneus',
  'carrosserie',
  'assurance',
  'autre',
] as const;

export type TypeIntervention = (typeof TYPES_INTERVENTION)[number];

export const LIBELLES_TYPE_INTERVENTION: Record<string, string> = {
  entretien: 'Entretien',
  reparation: 'Réparation',
  revision: 'Révision',
  controle_technique: 'Contrôle technique',
  pneus: 'Pneus',
  carrosserie: 'Carrosserie',
  assurance: 'Assurance',
  autre: 'Autre',
};

export function libelleType(type: string): string {
  return LIBELLES_TYPE_INTERVENTION[type] ?? type;
}

/** Gravité d'une échéance, du plus urgent au plus tranquille. */
export type UrgenceEcheance = 'depassee' | 'proche' | 'a_venir';

export interface Echeance {
  /** Clé stable, pour la retrouver d'un passage à l'autre. */
  cle: 'controle_technique' | 'assurance' | 'revision' | 'revision_km';
  label: string;
  urgence: UrgenceEcheance;
  /** Échéance par date, 'AAAA-MM-JJ', ou null pour une échéance kilométrique. */
  date: string | null;
  /** Jours restants ; négatif = dépassée. Null pour une échéance kilométrique. */
  jours: number | null;
  /** Kilomètres restants ; négatif = dépassée. Null pour une échéance par date. */
  km: number | null;
  /** Phrase toute faite, telle qu'elle s'affiche. */
  texte: string;
}

/** Au-delà de ce délai, une échéance passe de « à venir » à « proche ». */
const SEUIL_PROCHE_JOURS = 30;
/** Idem, pour une révision au kilométrage. */
const SEUIL_PROCHE_KM = 1_500;
/** Passé ce délai, un relevé kilométrique n'est plus fiable pour juger. */
export const RELEVE_PERIME_JOURS = 120;

/** Jours entiers entre aujourd'hui et une date 'AAAA-MM-JJ' ; négatif = passé. */
export function joursJusqua(jour: string | null, maintenantMs: number): number | null {
  if (!jour) return null;
  const cible = Date.parse(`${jour}T00:00:00Z`);
  if (Number.isNaN(cible)) return null;
  const d = new Date(maintenantMs);
  const debut = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return Math.round((cible - debut) / JOUR_MS);
}

function urgenceParJours(jours: number): UrgenceEcheance {
  if (jours < 0) return 'depassee';
  if (jours <= SEUIL_PROCHE_JOURS) return 'proche';
  return 'a_venir';
}

function texteJours(label: string, jours: number): string {
  if (jours < 0) return `${label} dépassé de ${String(-jours)} j`;
  if (jours === 0) return `${label} aujourd'hui`;
  return `${label} dans ${String(jours)} j`;
}

/** Le relevé kilométrique est-il trop vieux pour juger d'une échéance en km ? */
export function relevePerime(v: VehiculeSuivi, maintenantMs: number): boolean {
  const j = joursJusqua(v.kilometrageReleveLe, maintenantMs);
  return j === null || j < -RELEVE_PERIME_JOURS;
}

/**
 * Les échéances d'un véhicule, les plus urgentes d'abord.
 *
 * Une échéance absente ne produit rien : un véhicule sans date d'assurance
 * n'est pas « en retard d'assurance », il est simplement non renseigné, et le
 * CRM n'a pas à inventer une alerte là-dessus.
 */
export function echeancesVehicule(v: VehiculeSuivi, maintenantMs: number): Echeance[] {
  const out: Echeance[] = [];

  const parDate = (
    cle: Echeance['cle'],
    label: string,
    date: string | null,
  ): void => {
    const jours = joursJusqua(date, maintenantMs);
    if (jours === null || date === null) return;
    out.push({
      cle,
      label,
      urgence: urgenceParJours(jours),
      date,
      jours,
      km: null,
      texte: texteJours(label, jours),
    });
  };

  parDate('controle_technique', 'Contrôle technique', v.prochaineCt);
  parDate('assurance', 'Assurance', v.assuranceExpireLe);
  parDate('revision', 'Révision', v.prochaineRevisionLe);

  // Révision au kilométrage : elle ne vaut que si l'on a les deux nombres, et
  // que le relevé n'est pas d'un autre âge.
  if (v.prochaineRevisionKm !== null && v.kilometrage !== null && !relevePerime(v, maintenantMs)) {
    const km = v.prochaineRevisionKm - v.kilometrage;
    out.push({
      cle: 'revision_km',
      label: 'Révision',
      urgence: km < 0 ? 'depassee' : km <= SEUIL_PROCHE_KM ? 'proche' : 'a_venir',
      date: null,
      jours: null,
      km,
      texte:
        km < 0
          ? `Révision dépassée de ${String(-km)} km`
          : `Révision dans ${String(km)} km`,
    });
  }

  const rang: Record<UrgenceEcheance, number> = { depassee: 0, proche: 1, a_venir: 2 };
  return out.sort(
    (a, b) => rang[a.urgence] - rang[b.urgence] || (a.jours ?? a.km ?? 0) - (b.jours ?? b.km ?? 0),
  );
}

/** L'échéance la plus pressante, ou null si le véhicule n'en a aucune. */
export function echeanceLaPlusUrgente(v: VehiculeSuivi, maintenantMs: number): Echeance | null {
  return echeancesVehicule(v, maintenantMs)[0] ?? null;
}

export interface CoutsVehicule {
  /** Nombre d'interventions retenues. */
  nb: number;
  totalHt: number;
  totalTtc: number;
  /** Total HT par type d'intervention, du plus cher au moins cher. */
  parType: { type: string; label: string; nb: number; totalHt: number }[];
  /**
   * Kilomètres couverts par les interventions retenues (du plus petit au plus
   * grand kilométrage relevé). Null si moins de deux interventions portent un
   * kilométrage.
   */
  kmCouverts: number | null;
  /** Coût HT au kilomètre sur ces kilomètres-là. Null si non calculable. */
  coutParKm: number | null;
}

/** Arrondi au centime, pour ne pas traîner les décimales du binaire. */
const centimes = (n: number): number => Math.round(n * 100) / 100;

/**
 * Ce que coûte un véhicule, sur les interventions passées.
 *
 * `depuis` (une date 'AAAA-MM-JJ') restreint la période : sans lui, tout
 * l'historique compte.
 */
export function coutsVehicule(
  interventions: readonly InterventionVehicule[],
  depuis?: string,
): CoutsVehicule {
  const retenues = depuis
    ? interventions.filter((i) => i.dateIntervention >= depuis)
    : interventions.slice();

  let totalHt = 0;
  let totalTtc = 0;
  const parType = new Map<string, { nb: number; totalHt: number }>();
  const kms: number[] = [];

  for (const i of retenues) {
    totalHt += i.coutHt;
    totalTtc += i.coutTtc;
    const t = parType.get(i.type) ?? { nb: 0, totalHt: 0 };
    t.nb += 1;
    t.totalHt += i.coutHt;
    parType.set(i.type, t);
    if (i.kilometrage !== null) kms.push(i.kilometrage);
  }

  const kmCouverts =
    kms.length >= 2 ? Math.max(...kms) - Math.min(...kms) : null;

  return {
    nb: retenues.length,
    totalHt: centimes(totalHt),
    totalTtc: centimes(totalTtc),
    parType: [...parType.entries()]
      .map(([type, v]) => ({ type, label: libelleType(type), nb: v.nb, totalHt: centimes(v.totalHt) }))
      .sort((a, b) => b.totalHt - a.totalHt || a.label.localeCompare(b.label, 'fr')),
    kmCouverts,
    coutParKm: kmCouverts !== null && kmCouverts > 0 ? centimes(totalHt / kmCouverts) : null,
  };
}

/** Interventions rangées de la plus récente à la plus ancienne. */
export function trierInterventions(
  interventions: readonly InterventionVehicule[],
): InterventionVehicule[] {
  return interventions
    .slice()
    .sort(
      (a, b) =>
        b.dateIntervention.localeCompare(a.dateIntervention) ||
        (b.kilometrage ?? 0) - (a.kilometrage ?? 0),
    );
}

export interface LigneParc {
  vehicule: VehiculeSuivi;
  echeances: Echeance[];
  /** La plus pressante, celle qu'on affiche dans la liste. */
  urgente: Echeance | null;
  couts: CoutsVehicule;
  /** Le relevé kilométrique est trop vieux pour juger d'une échéance en km. */
  relevePerime: boolean;
}

/**
 * Le parc, véhicules les plus urgents en tête.
 *
 * Un véhicule hors service (`actif` faux) descend en bas de liste sans
 * disparaître : il peut rester à vendre, ou à assurer jusqu'à la revente.
 */
export function construireParc(
  vehicules: readonly VehiculeSuivi[],
  interventions: readonly InterventionVehicule[],
  maintenantMs: number,
): LigneParc[] {
  const parVehicule = new Map<string, InterventionVehicule[]>();
  for (const i of interventions) {
    const liste = parVehicule.get(i.vehiculeId) ?? [];
    liste.push(i);
    parVehicule.set(i.vehiculeId, liste);
  }

  const rang: Record<UrgenceEcheance, number> = { depassee: 0, proche: 1, a_venir: 2 };
  return vehicules
    .map((v) => {
      const ech = echeancesVehicule(v, maintenantMs);
      return {
        vehicule: v,
        echeances: ech,
        urgente: ech[0] ?? null,
        couts: coutsVehicule(parVehicule.get(v.id) ?? []),
        relevePerime: relevePerime(v, maintenantMs),
      };
    })
    .sort((a, b) => {
      if (a.vehicule.actif !== b.vehicule.actif) return a.vehicule.actif ? -1 : 1;
      const ra = a.urgente ? rang[a.urgente.urgence] : 3;
      const rb = b.urgente ? rang[b.urgente.urgence] : 3;
      if (ra !== rb) return ra - rb;
      return a.vehicule.immatriculation.localeCompare(b.vehicule.immatriculation, 'fr');
    });
}

/** Nom lisible d'un véhicule : « Renault Master — AB-123-CD ». */
export function nomVehicule(v: Pick<VehiculeSuivi, 'marque' | 'modele' | 'immatriculation'>): string {
  const modele = [v.marque, v.modele].filter(Boolean).join(' ').trim();
  return modele === '' ? v.immatriculation : `${modele} — ${v.immatriculation}`;
}
